import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  DataTableParamsWhere,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../shared/components/dynamic-form/question-base";
import { GenericInputModalComponent } from "../../../shared/components/generic-input-modal/generic-input-modal.component";
import { GenericModalComponent } from "../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import {
  SimpleDialogComponent,
  SimpleDialogProperties,
} from "../../../shared/components/simple-dialog/simple-dialog.component";
import { pattern } from "../../../shared/constants/patterns";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { CompanyService } from "../companies/company.service";
import { PartnerService } from "../partners/partner.service";
import { getUserSearchFilterOptions, userRights, userTypes } from "./user";
import { UserService } from "./user.service";
import { ChangePassword, User } from "./user.type";

@Injectable({
  providedIn: "root",
})
export class UserTableService {
  currentUser: UserInfo;
  dialogRefGeneric: MatDialogRef<GenericModalComponent>;
  dialogRefGenericInput: MatDialogRef<GenericInputModalComponent>;

  constructor(
    public notificationService: NotificationService,
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private userService: UserService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private dataTableService: DataTableService<User>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getUsersTableConfig = (config?: DataTableConfig): DataTable<User> => {
    const { id, hidden, shown, actionColumn, entities, selection, inlineEdit } = config || {};
    const columns: DataTableColumn<User>[] = (
      [
        {
          prop: "firstName",
          name: "First Name",
          inlineEdit: {
            control: "input",
            disabled: (user) => inlineEdit?.disabled?.(user),
            validators: () => [Validators.minLength(1), Validators.maxLength(200), Validators.pattern(pattern.words)],
          },
        },
        {
          prop: "lastName",
          name: "Last Name",
          inlineEdit: {
            control: "input",
            disabled: (user) => inlineEdit?.disabled?.(user),
            validators: () => [Validators.minLength(1), Validators.maxLength(200), Validators.pattern(pattern.words)],
          },
        },
        {
          prop: "phone",
          name: "Phone",
          inlineEdit: {
            control: "input",
            disabled: (user) => inlineEdit?.disabled?.(user),
            validators: () => [Validators.minLength(1), Validators.maxLength(255), Validators.pattern(pattern.words)],
          },
        },
        {
          prop: "email",
          name: "Email",
          link: {
            path: (user) => `/users/${user.id}`,
            disabled: (user) => user.deleted,
          },
        },
        {
          prop: "type",
          name: "User Type",
          render: (user) => userTypes.find((t) => t.value === user.type)?.label || "not-set",
        },
        {
          prop: "right",
          name: "User Right",
          render: (user) => userRights.find((r) => r.value === user.right)?.label,
        },
        {
          prop: "partner", // keep it as "partner", do not change, or it will break csv export and shown/hidden config
          name: "Partner",
          render: (user) => user.partner?.name || user.company?.partner?.name,
          hidden: !this.currentUser.isGlobal(),
          sortable: false,
          link: {
            path: this.managePartner,
          },
          exportCsv: {
            browserColumnKey: ["partner.name", "company.partner.name"],
            csvMapperKey: ["partner.name", "company.partner.name"],
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (user) => `/companies/${user.companyId}`,
            disabled: (user) => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
        {
          prop: "confirmed",
          name: "Confirmed",
          pipes: [{ clazz: BoolToStringPipe }],
          hidden: !this.currentUser.hasRight(["admin"]),
        },
        {
          prop: "activated",
          name: "Activated",
          pipes: [{ clazz: BoolToStringPipe }],
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      ] as DataTableColumn<User>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (user) => col?.inlineEdit?.disabled?.(user) || !this.canEdit(user),
        },
      }));

    return {
      id: id ?? "users",
      entityName: "User",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "users",
          data: {
            browser: (queryParams) =>
              this.userService.getUsers({
                ...queryParams,
                relations: { ...queryParams.relations, partner: true, company: { partner: true } },
                select: {
                  ...queryParams.select,
                  ...(this.currentUser.isGlobal() && {
                    partner: { name: true },
                    company: { name: true, partner: { id: true, name: true } },
                  }),
                  ...(!this.currentUser.isCompany() && { company: { name: true, partner: { id: true, name: true } } }),
                },
                where: [
                  { ...(entities?.company?.id && { companyId: entities?.company?.id }), ...queryParams.where },
                  ...(entities?.partner?.id ? [{ partnerId: entities?.partner?.id, ...queryParams.where }] : []),
                  ...(entities?.partner?.id
                    ? [{ company: { partnerId: entities?.partner?.id }, ...queryParams.where }]
                    : []),
                ] as DataTableParamsWhere<User>[],
                joinType: "LEFT",
              }),
            email: (queryParams) =>
              this.userService.getUsersQueue({
                ...queryParams,
                relations: { ...queryParams.relations, partner: true, company: { partner: true } },
                select: {
                  ...queryParams.select,
                  ...(this.currentUser.isGlobal() && {
                    partner: { name: true },
                    company: { name: true, partner: { id: true, name: true } },
                  }),
                  ...(!this.currentUser.isCompany() && { company: { name: true, partner: { id: true, name: true } } }),
                },
                where: [
                  { ...(entities?.company?.id && { companyId: entities?.company?.id }), ...queryParams.where },
                  ...(entities?.partner?.id ? [{ partnerId: entities?.partner?.id, ...queryParams.where }] : []),
                  ...(entities?.partner?.id
                    ? [{ company: { partnerId: entities?.partner?.id }, ...queryParams.where }]
                    : []),
                ] as DataTableParamsWhere<User>[],
                joinType: "LEFT",
              }),
          },
        },
      },
      columns,
      defaultColumns: ["deleted"].filter((col) => !hidden?.includes(col)),
      selection: {
        ...selection,
        selectedRows: [],
      },
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (user) => this.viewUser(user, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageUser,
              hidden: (user) => user.deleted || actionColumn?.view?.hidden,
            },
            {
              icon: "check",
              text: "Activate account",
              function: this.activateUser,
              hidden: (user) => user.deleted || user.activated,
            },
            {
              icon: "email",
              text: "Reset account",
              function: this.openSendRecoveryEmail,
              hidden: (user) => user.deleted,
            },
            {
              icon: "check_circle_outline",
              text: "Confirm account",
              function: this.confirmUser,
              hidden: (user) =>
                user.deleted ||
                !(
                  (this.currentUser.isGlobalAdmin() ||
                    this.currentUser.isPartnerAdmin() ||
                    this.currentUser.isCompanyAdmin()) &&
                  !user.confirmed
                ),
            },
            {
              icon: "lock",
              text: "Change password",
              function: this.changePasswordModal,
              hidden: (user) => !this.currentUser.isGlobalAdmin() || user.id === this.currentUser.id || user.deleted,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteUser,
              hidden: (user) =>
                actionColumn?.delete?.hidden || user.deleted || !allowDeleteEntity(user, this.currentUser),
            },
            {
              icon: "restore",
              text: "Restore",
              function: this.restoreUserModal,
              hidden: (user) => !(allowDeleteEntity(user, this.currentUser) && user.deleted),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getUserSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  // #region Table inputs
  getUsers = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    this.userService
      .getUsers({
        relations: { partner: true, company: { partner: true } },
        joinType: "LEFT",
        ...queryParams,
        where: queryParams.where,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  getUsersPost = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    this.userService
      .getUsersPost({
        relations: { partner: true, company: { partner: true } },
        joinType: "LEFT",
        ...queryParams,
        where: queryParams.where,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  getAvailableUsersToLinkToPartner = (partnerId: string, event: GetDataEvent<User>): void => {
    const { onError, onResult } = event;
    const queryParams = {
      ...event.queryParams,
      relations: {},
      where: {
        ...removePropsWithKeys<User>(event?.queryParams?.where),
      },
    };
    this.partnerService.getAvailableUsersToLink(partnerId, queryParams).subscribe(
      (result) => {
        onResult(result);
      },
      (error) => {
        onError(error);
      },
    );
  };

  getAvailableUsersToLinkToCompany = (companyId: string, event: GetDataEvent<User>): void => {
    const { onError, onResult } = event;
    const queryParams = {
      ...event.queryParams,
      relations: {},
      where: {
        ...removePropsWithKeys<User>(event?.queryParams?.where),
      },
    };
    this.companyService.getAvailableUsersToLink(companyId, queryParams).subscribe(
      (result) => {
        onResult(result);
      },
      (error) => {
        onError(error);
      },
    );
  };

  update = (event: UpdateDataEvent<User>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.userService.updateUserInformation(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };
  // #endregion

  /** Check if can edit a user (on the table) */

  private canEdit(user: User): boolean {
    if (this.currentUser.id !== user.id && !user.deleted) {
      if (
        this.currentUser.isGlobalAdminOrManager() ||
        this.currentUser.isPartnerAdmin() ||
        this.currentUser.isCompanyAdmin() ||
        (this.currentUser.isPartnerManager() && !!user.companyId)
      )
        return allowUpdateEntity(user, this.currentUser);
    }
    return false;
  }

  // #region Action methods

  private viewUser = (user: User, config?: DataTableConfig): void => {
    const columns = this.getUsersTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({ name: col.name, value: this.dataTableService.parseData(user, col, get(user, col.prop)) }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageUser = (user: User): void => {
    this.router.navigate([`/users/${user.id}`]);
  };

  private managePartner = (user: User): string => {
    return user.partnerId ? `/partners/${user.partnerId}` : `/partners/${user.company?.partnerId}`;
  };

  private activateUser = (user: User): void => {
    this.router.navigate([`/users/${user.id}/activate`]);
  };

  private deleteUser = (user: User, table: DataTableComponent<User>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "user",
        data: user,
        url: this.userService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private confirmUser = (user: User, table: DataTableComponent<User>): void => {
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "confirmUser",
      panelClass: "custom-dialog-container",
      data: {
        title: "Confirm User Account",
        message: `Do you want to confirm user account for ${user.email}?`,
        type: "yesNo",
        action: "accept",
      },
    });

    dialog.afterClosed().subscribe(async (result) => {
      if (result) {
        this.userService.confirmUser(user).subscribe(
          () => {
            table.search();
            this.notificationService.success(`User ${user.email} has been confirmed`);
          },
          (err) => {
            this.notificationService.error(err);
          },
        );
      }
    });
  };

  private openSendRecoveryEmail = (user: User): void => {
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "sendRecoveryEmail",
      panelClass: "custom-dialog-container",
      data: {
        title: "Reset user account",
        message: `Do you want to send reset user password email to ${user.email}?`,
        type: "yesNo",
        action: "cancelRenewal",
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.sendRecoveryEmail(user.email);
      }
    });
  };

  private sendRecoveryEmail(email: string): void {
    this.authenticationService.requestForgotPassword(email).subscribe(
      () => {
        this.notificationService.success(`A recovery password email has been sent to ${email}`);
      },
      (err) => {
        const message = err && err.message;
        this.notificationService.error(message || "An error has occurred");
      },
    );
  }

  private changePassword = (values: ChangePassword, user: User, table: DataTableComponent<User>): void => {
    const { password } = values;
    this.userService.changePasswordAdmin({ password }, user).subscribe(
      () => {
        this.dialogRefGeneric.close();
        table.search();
        this.notificationService.success("User password has been changed");
      },
      (err) => {
        this.notificationService.error(err);
      },
    );
  };

  private changePasswordModal = (user: User, table: DataTableComponent<User>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.userService.generateQuestionsChangePassword().subscribe((qs) => Object.assign(questions, qs));

    this.dialogRefGeneric = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Change password",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: ChangePassword) => {
                this.changePassword(values, user, table);
              },
            },
          ],
        },
      },
    });
  };

  private restoreUserModal = (user: User, table: DataTableComponent<User>): void => {
    this.dialogRefGenericInput = this.dialog.open(GenericInputModalComponent, {
      width: "450px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        action: "restore",
        name: user.email,
        operation: () => this.restoreUser(user, table),
        message: "restore the user and grant access to the platform",
      },
    });
  };

  private restoreUser(user: User, table: DataTableComponent<User>): void {
    this.userService.restoreUser(user.id).subscribe(
      () => {
        this.dialogRefGenericInput.close();
        table.search();
        this.notificationService.success("User has been restored");
      },
      (err) => {
        this.notificationService.error(err);
      },
    );
  }
  // #endregion
}
